// Utility functions for Candidate Input API
// Handles continuous updates of candidate work during interviews

interface SaveInputParams {
  interviewId: string
  sessionId: string
  inputType: 'code' | 'system_design' | 'whiteboard' | 'text_answer' | 'file_upload' | 'drawing'
  content?: string
  saveType?: 'auto' | 'manual' | 'checkpoint'
  language?: string
  problemId?: number
  questionId?: string
  problemTitle?: string
  inputCategory?: string
  diagramElements?: any[]
  designNotes?: string
  canvasElements?: any[]
  canvasSize?: { width: number; height: number }
  executionOutput?: string
  testResults?: any[]
  wordCount?: number
  linesOfCode?: number
  aiAssistanceUsed?: boolean
  aiQueriesCount?: number
  aiTokensUsed?: number
}

interface LoadedInputs {
  code: Record<string, any>
  systemDesign: Record<string, any>
  whiteboard: Record<string, any>
  textAnswers: Record<string, any>
  drawings: Record<string, any>
  fileUploads: Record<string, any>
}

import { 



buildApiUrl, getApiHeaders as getHeaders } from '@/config/api'

// Get authorization headers with content type
const getAuthHeaders = () => {
  return {
    ...getHeaders(true),
    'Content-Type': 'application/json'
  }
}

// Save candidate input (creates or updates existing input)
// NOTE: Use dedicated functions for code/whiteboard/system_design instead of this generic function
export const saveCandidateInput = async (params: SaveInputParams): Promise<boolean> => {
  try {
    const token = localStorage.getItem('token')
    if (!token) {
      console.warn('No auth token found')
      return false
    }

    const response = await fetch(buildApiUrl(`interview-sessions/public/save-input`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(params)
    })

    if (!response.ok) {
      const error = await response.json()
      console.error('Failed to save candidate input:', error)
      return false
    }

    const result = await response.json()
    if (params.saveType === 'manual' || params.saveType === 'checkpoint') {
      console.log('Input saved:', result.data.message)
    }
    return true
  } catch (error) {
    console.error('Error saving candidate input:', error)
    return false
  }
}

// Load candidate inputs for a session
export const loadCandidateInputs = async (interviewId: string, sessionId?: string): Promise<LoadedInputs | null> => {
  try {
    const token = localStorage.getItem('token')
    if (!token) return null

    const url = sessionId 
      ? buildApiUrl(`interview-sessions/${interviewId}/load-inputs?sessionId=${sessionId}`)
      : buildApiUrl(`interview-sessions/${interviewId}/load-inputs`)

    const response = await fetch(url, {
      headers: getAuthHeaders()
    })

    if (!response.ok) {
      const error = await response.json()
      console.error('Failed to load candidate inputs:', error)
      return null
    }

    const result = await response.json()
    return result.data.inputs
  } catch (error) {
    console.error('Error loading candidate inputs:', error)
    return null
  }
}

// Save plain notes (public, no auth)
export const saveNotesInput = async (
  interviewId: string,
  _sessionId: string,
  content: string,
  saveType: 'auto' | 'manual' | 'checkpoint' = 'auto'
): Promise<boolean> => {
  try {
    const response = await fetch(buildApiUrl('interview-responses/public/save/notes'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        interviewId,
        content,
        save_type: saveType,
        word_count: content.trim() ? content.trim().split(/\s+/).length : 0
      })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      console.error('Failed to save notes input:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('Error saving notes input:', error)
    return false
  }
}

export const loadNotesInput = async (
  interviewId: string,
  _sessionId: string
): Promise<LoadedNotes | null> => {
  try {
    const url = buildApiUrl(`interview-responses/public/load/notes?interviewId=${interviewId}`)
    const response = await fetch(url, { headers: { 'Content-Type': 'application/json' } })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      console.error('Failed to load notes input:', error)
      return null
    }

    const result = await response.json()
    const noteData = result?.data?.response
    if (!noteData || typeof noteData.content !== 'string') return null

    return {
      content: noteData.content,
      lastSaveAt: noteData.lastSaveAt || null
    }
  } catch (error) {
    console.error('Error loading notes input:', error)
    return null
  }
}

// Save code input using NEW dedicated coding API
export const saveCodeInput = async (
  interviewId: string, 
  sessionId: string, 
  code: string, 
  language: string,
  problemId?: number,
  problemTitle?: string,
  saveType: 'auto' | 'manual' | 'checkpoint' = 'auto',
  executionOutput?: string,
  testResults?: any[]
) => {
  try {
    const token = localStorage.getItem('token')
    if (!token) {
      console.warn('No auth token found')
      return false
    }

    // Use the NEW dedicated coding API endpoint
    const response = await fetch(buildApiUrl(`interview-sessions/public/save-coding-input`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        sessionId,
        content: code,
        saveType,
        language,
        problemId,
        problemTitle
      })
    })

    if (!response.ok) {
      const error = await response.json()
      console.error('Failed to save coding input:', error)
      return false
    }

    const result = await response.json()
    if (saveType === 'manual' || saveType === 'checkpoint') {
      console.log('Coding input saved:', result.data.message)
    }
    return true
  } catch (error) {
    console.error('Error saving coding input:', error)
    return false
  }
}

// Save system design input to interviews_responses
export const saveSystemDesignInput = async (
  interviewId: string, 
  sessionId: string, 
  elements: any[], 
  notes?: string,
  saveType: 'auto' | 'manual' | 'checkpoint' = 'auto'
) => {
  try {
    const token = localStorage.getItem('token')
    if (!token) {
      console.warn('No auth token found')
      return false
    }

    const response = await fetch(buildApiUrl(`interview-responses/public/save/system_design`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        interviewId,
        content: '',
        save_type: saveType,
        diagram_elements: {
          placedComponents: elements || [],
          connections: [],
          selectedTool: 'move',
          designMode: 'visual'
        },
        design_notes: notes || ''
      })
    })

    if (!response.ok) {
      const error = await response.json()
      console.error('Failed to save system design input:', error)
      return false
    }

    const result = await response.json()
    if (saveType === 'manual' || saveType === 'checkpoint') {
      console.log('System design input saved:', result.data.message)
    }
    return true
  } catch (error) {
    console.error('Error saving system design input:', error)
    return false
  }
}

// Save whiteboard input to interviews_responses
export const saveWhiteboardInput = async (
  interviewId: string, 
  sessionId: string, 
  elements: any[], 
  canvasSize: { width: number; height: number },
  saveType: 'auto' | 'manual' | 'checkpoint' = 'auto'
) => {
  try {
    const token = localStorage.getItem('token')
    if (!token) {
      console.warn('No auth token found')
      return false
    }

    const response = await fetch(buildApiUrl(`interview-responses/public/save/whiteboard`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        interviewId,
        content: '',
        save_type: saveType,
        canvas_elements: elements || [],
        canvas_size: canvasSize || {}
      })
    })

    if (!response.ok) {
      const error = await response.json()
      console.error('Failed to save whiteboard input:', error)
      return false
    }

    const result = await response.json()
    if (saveType === 'manual' || saveType === 'checkpoint') {
      console.log('Whiteboard input saved:', result.data.message)
    }
    return true
  } catch (error) {
    console.error('Error saving whiteboard input:', error)
    return false
  }
}

// Save text answer
export const saveTextAnswer = async (
  interviewId: string, 
  sessionId: string, 
  content: string, 
  questionId?: string,
  category: string = 'default',
  saveType: 'auto' | 'manual' | 'checkpoint' = 'auto'
) => {
  return saveCandidateInput({
    interviewId,
    sessionId,
    inputType: 'text_answer',
    inputCategory: category,
    content,
    questionId,
    wordCount: content.split(/\s+/).filter(word => word.length > 0).length,
    saveType
  })
}

// Start a new interview session
export const startInterviewSession = async (
  interviewId: string, 
  sessionType: 'video' | 'coding' | 'whiteboard' | 'system_design' | 'behavioral',
  recordingConsent: boolean = false,
  candidateId?: number
) => {
  try {
    const response = await fetch(buildApiUrl(`interview-sessions/${interviewId}/start`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        sessionType,
        recordingConsent,
        candidateId
      })
    })

    if (!response.ok) {
      const error = await response.json()
      console.error('Failed to start interview session:', error)
      return null
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error starting interview session:', error)
    return null
  }
}

// End interview session
export const endInterviewSession = async (
  interviewId: string, 
  sessionId: string,
  feedback?: string,
  rating?: number
) => {
  try {
    const response = await fetch(buildApiUrl(`interview-sessions/${interviewId}/end`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        sessionId,
        feedback,
        rating
      })
    })

    if (!response.ok) {
      const error = await response.json()
      console.error('Failed to end interview session:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('Error ending interview session:', error)
    return false
  }
}

// Get current session status
export const getCurrentSession = async (interviewId: string) => {
  try {
    const response = await fetch(buildApiUrl(`interview-sessions/${interviewId}/current-session`), {
      headers: getAuthHeaders()
    })

    if (!response.ok) {
      const error = await response.json()
      console.error('Failed to get current session:', error)
      return null
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('Error getting current session:', error)
    return null
  }
}
